import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import loginHero from "../assets/login_hero.png";
import AppIcon from "../assets/sayq-fav.svg";
import "../App.css";

const generateGuestId = () => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < 20; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const API = "https://api18081113.sayq.pl";

export default function GuestRegister() {
  const { secretId } = useParams();
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [themeColor, setThemeColor] = useState("");
  const [themeMode, setThemeMode] = useState("light");
  const [isTrialMode, setIsTrialMode] = useState(false);
  const navigate = useNavigate();

  // Redirect if no secretId
  useEffect(() => {
    if (!secretId) {
      navigate("/");
    }
  }, [secretId, navigate]);

  // Apply Theme Mode (Dark/Light)
  useEffect(() => {
    const styleId = 'dark-mode-styles';
    const existingStyle = document.getElementById(styleId);

    if (themeMode === 'dark') {
      document.body.classList.add('dark-mode');
      if (!existingStyle) {
        const style = document.createElement('style');
        style.id = styleId;
        style.innerHTML = `
                body, .page-root { background-color: #121212 !important; color: #e0e0e0 !important; }
                .bg-white { background-color: #1e1e1e !important; color: #e0e0e0 !important; }
                .text-dark { color: #f8f9fa !important; }
                .text-secondary { color: #adb5bd !important; }
                .border-light { border-color: #333 !important; }
                .bg-light { background-color: #2c2c2c !important; }
                .form-control, .minimal-input { 
                    background-color: #2c2c2c !important; 
                    border-color: #444 !important; 
                    color: #fff !important; 
                }
                .btn-light { background-color: #333 !important; border-color: #444 !important; color: #fff !important; }
                .shadow-sm { box-shadow: 0 .125rem .25rem rgba(0,0,0,.5) !important; }
                /* Guest Register Specifics */
                .form-control-lg { background-color: #2c2c2c !important; color: #fff !important; border-color: #444 !important; }
            `;
        document.head.appendChild(style);
      }
    } else {
      document.body.classList.remove('dark-mode');
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    }

    return () => {
      // Cleanup handled by re-run
    };
  }, [themeMode]);

  // Apply Theme Color
  useEffect(() => {
    if (themeColor) {
      const hex = themeColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      document.documentElement.style.setProperty('--bs-primary', themeColor);
      document.documentElement.style.setProperty('--bs-primary-rgb', `${r}, ${g}, ${b}`);
      document.documentElement.style.setProperty('--primary-color', themeColor);

      // Dynamic style for Guest Register specific elements
      const style = document.createElement('style');
      style.innerHTML = `
         .btn-primary { background-color: ${themeColor} !important; border-color: ${themeColor} !important; }
         .text-primary { color: ${themeColor} !important; }
         .bg-primary { background-color: ${themeColor} !important; }
         .shadow-primary { box-shadow: 0 4px 14px 0 rgba(${r}, ${g}, ${b}, 0.39) !important; }
         .focus-ring:focus { box-shadow: 0 0 0 4px rgba(${r}, ${g}, ${b}, 0.1) !important; }
       `;
      document.head.appendChild(style);
      return () => {
        document.documentElement.style.removeProperty('--bs-primary');
        document.documentElement.style.removeProperty('--bs-primary-rgb');
        document.documentElement.style.removeProperty('--primary-color');
        if (document.head.contains(style)) document.head.removeChild(style);
      };
    }
  }, [themeColor]);

  // Fetch Event Data
  useEffect(() => {
    const fetchEventData = async () => {
      try {
        const res = await fetch(`${API}/event-info/${secretId}?t=${Date.now()}`, { cache: "no-store", mode: "cors" });
        const data = await res.json();

        if (res.ok) {
          if (data.theme_color) setThemeColor(data.theme_color);
          if (data.theme_mode) setThemeMode(data.theme_mode);

          const rawImg = data["event-img"] || data["event_img"];
          const eventImgUrl = rawImg
            ? (String(rawImg).startsWith("http") ? rawImg : `https://eventicon.sayq.pl/${rawImg}`)
            : null;

          if (data.trial_ends_at) {
            const trialEnds = new Date(data.trial_ends_at);
            setIsTrialMode(trialEnds > new Date());
          } else {
            setIsTrialMode(false);
            localStorage.removeItem(`isTrial_${secretId}`);
          }

          setEventData({
            ...data,
            event_img: eventImgUrl
          });
        } else {
          console.error("Błąd pobierania event info:", data.error);
        }
      } catch (err) {
        console.error("Błąd połączenia z event-info:", err);
      }
    };

    if (secretId) fetchEventData();
  }, [secretId]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Wprowadź swoje imię");
      return;
    }

    setLoading(true);

    try {
      const checkRes = await fetch(
        `${API}/check-guest/${secretId}/${encodeURIComponent(name)}`
      );
      const checkData = await checkRes.json();

      if (checkData.exists) {
        setError("Ta nazwa jest już zajęta. Wybierz inną.");
        setLoading(false);
        return;
      }

      const guestId = generateGuestId();

      const addRes = await fetch(`${API}/add-guest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ secretId, name, guestId }),
      });

      const addData = await addRes.json().catch(() => ({}));

      if (!addRes.ok) {
        setError(addData?.error || "Błąd podczas dołączania. Spróbuj ponownie.");
        setLoading(false);
        return;
      }

      // Success
      document.cookie = `guestID=${guestId}; path=/; max-age=31536000`;
      localStorage.setItem("guestID", guestId);
      localStorage.setItem("guestName", name);
      localStorage.setItem("secretId", secretId);

      navigate("/chat");
    } catch (err) {
      console.error("Błąd połączenia:", err);
      setError("Błąd połączenia z serwerem.");
      setLoading(false);
    }
  };

  return (
    <div className="d-flex w-100 overflow-hidden bg-white font-sans page-root-fixed">
      {/* LEFT COLUMN: FORM */}
      <div className="col-12 col-md-6 d-flex flex-column justify-content-center align-items-center p-4 p-md-5 relative overflow-y-auto">

        <div className="w-100" style={{ maxWidth: "420px" }}>

          {/* Branding / Event Logo */}
          <div className="mb-4 text-center text-md-start">
            {eventData?.event_img ? (
              <img
                src={eventData.event_img}
                alt="Event Logo"
                className="rounded-4 shadow-sm object-fit-cover"
                style={{ width: 80, height: 80 }}
              />
            ) : (
              <img src={AppIcon} alt="SayQ" className="rounded-3" style={{ width: 64, height: 64, objectFit: 'contain' }} />
            )}
          </div>

          {isTrialMode && (
            <div className="alert alert-warning py-2 text-center text-sm mb-4 rounded-3 border-0 bg-warning-subtle text-warning-emphasis fw-bold shadow-sm">
              Wydarzenie w trybie testowym
            </div>
          )}

          <h1 className="fw-bold mb-2 text-dark text-center text-md-start" style={{ fontSize: '2rem' }}>
            Witaj!
          </h1>
          <p className="text-secondary mb-5 text-center text-md-start">
            Dołączasz do wydarzenia <span className="fw-bold text-dark">{eventData?.event_name || "..."}</span>.
          </p>

          <form onSubmit={handleRegister}>
            <div className="mb-4">
              <label htmlFor="guestName" className="form-label text-uppercase small fw-bold text-secondary ls-1" style={{ fontSize: '0.75rem' }}>
                Twoje Imię / Nick
              </label>
              <input
                id="guestName"
                type="text"
                className="form-control form-control-lg bg-light border-0 rounded-3 py-3 px-3 transition-all focus-ring"
                placeholder="np. Kasia"
                value={name}
                onChange={e => setName(e.target.value)}
                autoFocus
              />
            </div>

            {error && (
              <div className="alert alert-danger py-2 text-center text-sm mb-4 rounded-3 border-0 bg-danger-subtle text-danger fw-bold animate-shake">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary w-100 btn-lg rounded-pill fw-bold py-3 mb-4 shadow-primary hover-lift transition-all"
              disabled={loading}
              style={{ fontSize: '1rem', letterSpacing: '0.5px' }}
            >
              {loading ? (
                <span><span className="spinner-border spinner-border-sm me-2" />Dołączanie...</span>
              ) : (
                "DOŁĄCZ DO WYDARZENIA"
              )}
            </button>
          </form>

          <div className="mt-4 text-center text-secondary small opacity-75">
            Biorąc udział, akceptujesz regulamin wydarzenia.
          </div>

        </div>
      </div>

      {/* RIGHT COLUMN: HERO IMAGE */}
      <div className="d-none d-md-block col-md-6 p-0 position-relative bg-dark overflow-hidden">
        <img
          src={loginHero}
          alt="Event Mood"
          className="w-100 h-100 object-fit-cover opacity-75 animate-zoom-slow"
          style={{ objectPosition: 'center' }}
        />
        <div className="position-absolute top-0 start-0 w-100 h-100" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)' }}></div>

        <div className="position-absolute bottom-0 start-0 w-100 p-5 mb-5 text-white">
          <h2 className="display-4 fw-bold mb-3">Bądź częścią<br />rozmowy.</h2>
          <p className="lead opacity-75" style={{ maxWidth: 400 }}>
            Zadawaj pytania, głosuj i baw się dobrze na wydarzeniu.
          </p>
        </div>
      </div>

      <style>{`
                .page-root-fixed { height: 100dvh; }
                .font-sans { font-family: 'Check', 'Inter', system-ui, -apple-system, sans-serif; }
                .ls-1 { letter-spacing: 1px; }
                
                .hover-lift:hover { transform: translateY(-2px); box-shadow: 0 10px 25px -5px rgba(37, 99, 235, 0.4); }
                .shadow-primary { box-shadow: 0 4px 14px 0 rgba(37, 99, 235, 0.39); }
                
                .focus-ring:focus { box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.1); background-color: #fff; }
                
                @keyframes zoomSlow { 0% { transform: scale(1); } 100% { transform: scale(1.05); } }
                .animate-zoom-slow { animation: zoomSlow 20s ease-in-out infinite alternate; }
                
                @keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-5px); } 75% { transform: translateX(5px); } }
                .animate-shake { animation: shake 0.3s ease-in-out; }
            `}</style>
    </div>
  );
}
